import { lazy, Suspense } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { QueryClientProvider } from '@tanstack/react-query'
import { Toaster } from 'sonner'
import { queryClient } from './lib/api/queryClient'
import { AppLayout } from './components/layout/AppLayout'

const LoginPage          = lazy(() => import('./features/auth/LoginPage'))
const ForgotPasswordPage = lazy(() => import('./features/auth/ForgotPasswordPage'))
const RegisterPage       = lazy(() => import('./features/registration/RegisterPage'))

const UserDashboard   = lazy(() => import('./features/dashboard/UserDashboard'))
const ProfilePage     = lazy(() => import('./features/user/ProfilePage'))
const SchedulePage    = lazy(() => import('./features/user/SchedulePage'))
const ScheduleRegisterPage = lazy(() => import('./features/user/ScheduleRegisterPage'))
const QuizPage        = lazy(() => import('./features/user/QuizPage'))
const QuizRunnerPage  = lazy(() => import('./features/user/QuizRunnerPage'))
const QuizHistoryPage = lazy(() => import('./features/user/QuizHistoryPage'))
const QuizResultPage  = lazy(() => import('./features/user/QuizResultPage'))
const CertificatePage = lazy(() => import('./features/user/CertificatePage'))

const AdminDashboard  = lazy(() => import('./features/dashboard/AdminDashboard'))
const UsersListPage   = lazy(() => import('./features/admin/UsersListPage'))
const UserDetailPage  = lazy(() => import('./features/admin/UserDetailPage'))
const AdminsListPage  = lazy(() => import('./features/admin/AdminsListPage'))
const ExamsPage           = lazy(() => import('./features/admin/ExamsPage'))
const ExamDetailPage      = lazy(() => import('./features/admin/ExamDetailPage'))
const ExamSetQuestionsPage = lazy(() => import('./features/admin/ExamSetQuestionsPage'))
const QuizLibraryPage     = lazy(() => import('./features/admin/QuizLibraryPage'))
const QuizMonitorPage = lazy(() => import('./features/admin/QuizMonitorPage'))
const ExamApplicantsPage  = lazy(() => import('./features/admin/ExamApplicantsPage'))
const ExamFieldsPage      = lazy(() => import('./features/admin/ExamFieldsPage'))
const ExamFieldDetailPage = lazy(() => import('./features/admin/ExamFieldDetailPage'))
const FieldApplicantsPage = lazy(() => import('./features/admin/FieldApplicantsPage'))
const AnnouncementsPage   = lazy(() => import('./features/admin/AnnouncementsPage'))
const AuditLogPage        = lazy(() => import('./features/admin/AuditLogPage'))
const ReportsPage         = lazy(() => import('./features/admin/ReportsPage'))

function Loading() {
  return (
    <div className="min-h-screen grid place-items-center">
      <div className="text-sm text-ink-mute">กำลังโหลด…</div>
    </div>
  )
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Toaster
        position="top-center"
        richColors
        closeButton
        toastOptions={{ className: 'text-sm', duration: 4000 }}
      />
      <BrowserRouter basename="/app">
        <Suspense fallback={<Loading />}>
          <Routes>
            {/* public */}
            <Route path="/login"    element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/forgot"   element={<ForgotPasswordPage />} />

            {/* user */}
            <Route path="/"             element={<AppLayout requireRole="user"><UserDashboard /></AppLayout>} />
            <Route path="/profile"      element={<AppLayout requireRole="user"><ProfilePage /></AppLayout>} />
            <Route path="/schedule"     element={<AppLayout requireRole="user"><SchedulePage /></AppLayout>} />
            <Route path="/schedule/:id/register" element={<AppLayout requireRole="user"><ScheduleRegisterPage /></AppLayout>} />
            <Route path="/quiz"         element={<AppLayout requireRole="user"><QuizPage /></AppLayout>} />
            <Route path="/quiz/attempt/:id" element={<AppLayout requireRole="user"><QuizRunnerPage /></AppLayout>} />
            <Route path="/quiz-history"  element={<AppLayout requireRole="user"><QuizHistoryPage /></AppLayout>} />
            <Route path="/quiz-result"  element={<AppLayout requireRole="user"><QuizResultPage /></AppLayout>} />
            <Route path="/certificate"  element={<AppLayout requireRole="user"><CertificatePage /></AppLayout>} />

            {/* admin */}
            <Route path="/admin"             element={<AppLayout requireRole="admin"><AdminDashboard /></AppLayout>} />
            <Route path="/admin/exams"            element={<AppLayout requireRole="admin"><ExamsPage /></AppLayout>} />
            <Route path="/admin/exams/:id"             element={<AppLayout requireRole="admin"><ExamDetailPage /></AppLayout>} />
            <Route path="/admin/exam-sets/:id/questions" element={<AppLayout requireRole="admin"><ExamSetQuestionsPage /></AppLayout>} />
            <Route path="/admin/question-library"        element={<AppLayout requireRole="admin"><QuizLibraryPage /></AppLayout>} />
            <Route path="/admin/quiz-monitor" element={<AppLayout requireRole="admin"><QuizMonitorPage /></AppLayout>} />
            <Route path="/admin/exams/:id/applicants"  element={<AppLayout requireRole="admin"><ExamApplicantsPage /></AppLayout>} />
            <Route path="/admin/exam-fields"      element={<AppLayout requireRole="admin"><ExamFieldsPage /></AppLayout>} />
            <Route path="/admin/exam-fields/:id"             element={<AppLayout requireRole="admin"><ExamFieldDetailPage /></AppLayout>} />
            <Route path="/admin/exam-fields/:id/applicants"  element={<AppLayout requireRole="admin"><FieldApplicantsPage /></AppLayout>} />
            <Route path="/admin/announcements"    element={<AppLayout requireRole="admin"><AnnouncementsPage /></AppLayout>} />
            <Route path="/admin/audit-log"        element={<AppLayout requireRole="admin"><AuditLogPage /></AppLayout>} />
            <Route path="/admin/users"       element={<AppLayout requireRole="admin"><UsersListPage /></AppLayout>} />
            <Route path="/admin/users/:id"   element={<AppLayout requireRole="admin"><UserDetailPage /></AppLayout>} />
            <Route path="/admin/admins"      element={<AppLayout requireRole="admin"><AdminsListPage /></AppLayout>} />
            <Route path="/admin/reports"     element={<AppLayout requireRole="admin"><ReportsPage /></AppLayout>} />

            <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </QueryClientProvider>
  )
}
